/**
 * Credential Recovery for Phenom Drop
 *
 * When C2PA parsing fails (e.g., metadata was stripped from the file),
 * attempt recovery via the SanMarcSoft Manifest Store.
 *
 * Recovery methods:
 *   - File hash (SHA-256) lookup
 *   - Perceptual fingerprint (pHash) for images
 *
 * The Manifest Store is public (no auth required), hosted on Scaleway (EU).
 */

const MANIFEST_STORE_URL = 'https://manifests.sanmarcsoft.com/v1'

export interface RecoveredManifest {
  manifestId: string
  signerCn: string | null
  signedAt: string | null
  fileHash: string | null
  filename: string | null
  fileType: string | null
  c2paSpecVersion: string
  recoveryMethod: 'fileHash' | 'fingerprint'
}

export interface RecoveryResult {
  recovered: boolean
  manifest: RecoveredManifest | null
  explanatoryNote: string
}

const NO_RECOVERY: RecoveryResult = {
  recovered: false,
  manifest: null,
  explanatoryNote: '',
}

/**
 * Attempt credential recovery by file hash (SHA-256).
 * Queries the Manifest Store for a manifest matching the exact file hash.
 */
export async function attemptRecoveryByFileHash(
  fileHash: string
): Promise<RecoveryResult> {
  try {
    const url = `${MANIFEST_STORE_URL}/matches/byBinding?alg=sha256&value=${fileHash}`
    const res = await fetch(url)
    if (!res.ok) return NO_RECOVERY

    const data = await res.json()
    const matches = data.matches || []
    if (matches.length === 0) return NO_RECOVERY

    const best = matches[0]
    return await fetchAndBuildResult(best.manifestId, 'fileHash')
  } catch {
    return NO_RECOVERY
  }
}

/**
 * Attempt credential recovery by perceptual hash (pHash).
 * Computes pHash from a canvas thumbnail and queries the Manifest Store.
 */
export async function attemptRecoveryByFingerprint(
  imageData: ImageData
): Promise<RecoveryResult> {
  try {
    // Simple pHash: resize to 8x8 greyscale, compute DCT, threshold
    const phash = computeSimplePhash(imageData)

    const url = `${MANIFEST_STORE_URL}/matches/byBinding?alg=phash&value=${phash}`
    const res = await fetch(url)
    if (!res.ok) return NO_RECOVERY

    const data = await res.json()
    const matches = data.matches || []
    if (matches.length === 0) return NO_RECOVERY

    const best = matches[0]
    if (best.similarityScore < 90) return NO_RECOVERY

    return await fetchAndBuildResult(best.manifestId, 'fingerprint')
  } catch {
    return NO_RECOVERY
  }
}

/**
 * Combined recovery: try file hash first, then perceptual fingerprint for images.
 */
export async function attemptCredentialRecovery(
  fileHash: string,
  file: File
): Promise<RecoveryResult> {
  // Try file hash first (exact match)
  const hashResult = await attemptRecoveryByFileHash(fileHash)
  if (hashResult.recovered) return hashResult

  // For images, try perceptual fingerprint
  if (file.type.startsWith('image/')) {
    const imageData = await fileToImageData(file)
    if (imageData) {
      return await attemptRecoveryByFingerprint(imageData)
    }
  }

  return NO_RECOVERY
}

async function fetchAndBuildResult(
  manifestId: string,
  method: 'fileHash' | 'fingerprint'
): Promise<RecoveryResult> {
  const manifestRes = await fetch(
    `${MANIFEST_STORE_URL}/manifests/${manifestId}?format=json`
  )
  if (!manifestRes.ok) return NO_RECOVERY

  const manifest = await manifestRes.json()

  return {
    recovered: true,
    manifest: {
      manifestId,
      signerCn: manifest.signerCn || null,
      signedAt: manifest.signedAt || null,
      fileHash: manifest.fileHash || null,
      filename: manifest.filename || null,
      fileType: manifest.fileType || null,
      c2paSpecVersion: manifest.c2paSpecVersion || '2.0',
      recoveryMethod: method,
    },
    explanatoryNote:
      method === 'fileHash'
        ? "This file's Content Credentials were verified via its cryptographic hash. " +
          'The original credentials confirm who signed it and when.'
        : "This file's original Content Credentials were removed (typically by a platform " +
          'or file conversion), but we recovered them using a visual fingerprint. ' +
          'The recovered credentials confirm who signed the original and when.',
  }
}

/** Check if a signer CN belongs to the Phenom/SanMarcSoft ecosystem */
export function isEcosystemSigner(signerCn: string | null): boolean {
  if (!signerCn) return false
  const lower = signerCn.toLowerCase()
  return (
    lower.includes('verifieddit') ||
    lower.includes('trusteddit') ||
    lower.includes('thephenom') ||
    lower.includes('sanmarcsoft') ||
    lower.includes('phenom')
  )
}

// ---- Internal helpers ----

function fileToImageData(file: File): Promise<ImageData | null> {
  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(url)
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      resolve(data)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }

    img.src = url
  })
}

/**
 * Simple perceptual hash (pHash): resize to 32x32 greyscale,
 * compute mean, threshold to 64-bit hash, return as 16-char hex.
 */
function computeSimplePhash(imageData: ImageData): string {
  const size = 8
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!

  // Create a temporary canvas with the source image
  const srcCanvas = document.createElement('canvas')
  srcCanvas.width = imageData.width
  srcCanvas.height = imageData.height
  const srcCtx = srcCanvas.getContext('2d')!
  srcCtx.putImageData(imageData, 0, 0)

  // Resize to 8x8
  ctx.drawImage(srcCanvas, 0, 0, size, size)
  const small = ctx.getImageData(0, 0, size, size)

  // Convert to greyscale values
  const grey: number[] = []
  for (let i = 0; i < small.data.length; i += 4) {
    grey.push(small.data[i] * 0.299 + small.data[i + 1] * 0.587 + small.data[i + 2] * 0.114)
  }

  // Compute mean
  const mean = grey.reduce((a, b) => a + b, 0) / grey.length

  // Threshold: each pixel above mean = 1, below = 0
  let hash = BigInt(0)
  for (let i = 0; i < grey.length; i++) {
    if (grey[i] > mean) {
      hash |= BigInt(1) << BigInt(63 - i)
    }
  }

  return hash.toString(16).padStart(16, '0')
}
